import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { getActivityProgress, markSlideViewed } from '../utils/progressTracker';

const ACTIVITY_ID = 'bac-assp-pse-approche';
const titleImage = 'https://static.prod-images.emergentagent.com/jobs/1705ebef-a469-4df3-8a71-5995b31fd754/images/51a17746d343a9e5cee499b4340c625a63cd73d58cd49f3406e7c389e85e459a.png';
const bgImage = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800';

const theme = { primary: '#3B82F6', secondary: '#1D4ED8', text: '#1E3A5F', bg: '#F0F7FF' };

const slides = [
  // ===== TITRE =====
  { type: 'title', image: titleImage, blocTitle: "PRÉVENTION SANTÉ ENVIRONNEMENT (PSE)" },

  // ===== SOMMAIRE =====
  { type: 'sommaire', sections: [
    { num: "1", title: "Santé et déterminants", target: 3 },
    { num: "2", title: "Prévention des risques professionnels", target: 5 },
    { num: "3", title: "Méthode d'analyse des risques (5M – Ishikawa)", target: 9 },
    { num: "4", title: "Accidents du travail et maladies professionnelles", target: 13 },
    { num: "5", title: "Risques spécifiques en ASSP", target: 16 },
    { num: "6", title: "Conduites à risque et hygiène de vie", target: 25 },
    { num: "7", title: "Environnement et développement durable", target: 27 },
    { num: "8", title: "Conduite à tenir en cas d'urgence", target: 29 },
    { num: "9", title: "Cas pratiques professionnels", target: 31 },
  ]},

  // =============================================
  // 1. SANTÉ ET DÉTERMINANTS
  // =============================================
  { type: 'section', section: "1", title: "Santé et déterminants" },
  { type: 'richtext', title: "Les déterminants de santé", blocks: [
    { text: "La santé est un état de bien-être global qui dépend de plusieurs facteurs :", list: [
      "Biologiques : âge, sexe, antécédents",
      "Psychologiques : estime de soi, stress, santé mentale",
      "Sociaux : revenus, logement, entourage, éducation",
      "Environnementaux : pollution, bruit, sécurité, habitat",
    ]},
    { text: "Dans le secteur ASSP, le professionnel agit sur ces déterminants au quotidien : hygiène, alimentation, sommeil, environnement sécurisé, relation d'aide." },
    { note: "L'objectif du PSE est de comprendre que la santé ne dépend pas uniquement des soins, mais aussi des conditions de vie." },
  ]},

  // =============================================
  // 2. PRÉVENTION DES RISQUES PROFESSIONNELS
  // =============================================
  { type: 'section', section: "2", title: "Prévention des risques professionnels" },
  { type: 'richtext', title: "Les types de risques", blocks: [
    { text: "Le professionnel est exposé à plusieurs types de risques :", list: [
      "Biologiques : virus, bactéries (soins, toilettes, change)",
      "Chimiques : produits d'entretien",
      "Physiques : chutes, manutention, bruit",
      "Psychosociaux : stress, fatigue, charge émotionnelle",
    ]},
  ]},
  { type: 'richtext', title: "Les mesures de prévention", blocks: [
    { text: "La prévention repose sur :", list: [
      "L'hygiène des mains",
      "L'utilisation des EPI (Équipements de Protection Individuelle)",
      "Le respect des protocoles",
      "L'ergonomie",
      "La communication en équipe",
    ]},
  ]},
  { type: 'richtext', title: "Exemple concret", blocks: [
    { text: "Porter des gants lors d'un change limite le risque infectieux." },
    { note: "La prévention n'est pas une option, c'est une obligation professionnelle quotidienne." },
  ]},

  // =============================================
  // 3. MÉTHODE DES 5M (ISHIKAWA)
  // =============================================
  { type: 'section', section: "3", title: "Méthode d'analyse des risques (5M – Ishikawa)" },
  { type: 'richtext', title: "Les 5 catégories de causes", blocks: [
    { text: "La méthode des 5M permet d'analyser un problème ou un accident pour en comprendre les causes. Elle repose sur 5 catégories :", list: [
      "Main-d'œuvre : erreur humaine, fatigue, manque de formation",
      "Matériel : équipement défectueux ou inadapté",
      "Méthode : protocole non respecté",
      "Milieu : environnement (sol glissant, espace encombré)",
      "Matière : produits utilisés",
    ]},
  ]},
  { type: 'richtext', title: "Exemple concret : chute d'une personne âgée", blocks: [
    { text: "Analyse par les 5M :", list: [
      "Sol mouillé (Milieu)",
      "Absence de surveillance (Main-d'œuvre)",
      "Chaussures inadaptées (Matière)",
    ]},
  ]},
  { type: 'richtext', title: "Un outil essentiel", blocks: [
    { note: "Cette méthode est très attendue en examen et en VAE. Entraînez-vous à analyser des situations concrètes avec les 5M." },
  ]},

  // =============================================
  // 4. ACCIDENTS DU TRAVAIL ET MALADIES PROFESSIONNELLES
  // =============================================
  { type: 'section', section: "4", title: "Accidents du travail et maladies professionnelles" },
  { type: 'richtext', title: "Définitions et exemples", blocks: [
    { heading: "Accident du travail", text: "Un événement soudain survenu dans le cadre du travail." },
    { heading: "Maladie professionnelle", text: "Se développe progressivement (ex : TMS)." },
    { text: "Exemples en ASSP :", list: [
      "Mal de dos lié aux transferts",
      "Stress chronique",
      "Blessure lors d'un soin",
    ]},
  ]},
  { type: 'richtext', title: "Le rôle du professionnel", blocks: [
    { text: "Le professionnel doit :", list: [
      "Signaler l'accident",
      "Analyser les causes",
      "Participer à la prévention",
    ]},
    { note: "L'objectif n'est pas de « subir » mais de comprendre et éviter." },
  ]},

  // =============================================
  // 5. RISQUES SPÉCIFIQUES EN ASSP
  // =============================================
  { type: 'section', section: "5", title: "Risques spécifiques en ASSP" },

  { type: 'subsection', title: "Troubles musculo-squelettiques (TMS)" },
  { type: 'richtext', title: "TMS : 1ère cause de maladie professionnelle", blocks: [
    { text: "Les TMS sont liés aux gestes répétitifs et à la manutention.", list: [
      "Zones touchées : dos (lombalgies), épaules, poignets, genoux",
      "Prévention : posture adaptée, matériel d'aide, aides techniques",
    ]},
  ]},

  { type: 'subsection', title: "Risque infectieux" },
  { type: 'richtext', title: "Se protéger des infections", blocks: [
    { text: "Prévention du risque infectieux :", list: [
      "Hygiène rigoureuse des mains",
      "Port des EPI (gants, masque, surblouse)",
      "Respect des protocoles de soins",
    ]},
  ]},

  { type: 'subsection', title: "Risque de chute" },
  { type: 'richtext', title: "Prévenir les chutes", blocks: [
    { text: "Mesures de prévention :", list: [
      "Environnement sécurisé (sol, éclairage, obstacles)",
      "Surveillance adaptée au public",
    ]},
  ]},

  { type: 'subsection', title: "Risques psychosociaux" },
  { type: 'richtext', title: "Préserver sa santé mentale", blocks: [
    { text: "Les risques psychosociaux comprennent :", list: [
      "Fatigue émotionnelle",
      "Surcharge de travail",
      "Gestion du stress au quotidien",
    ]},
    { note: "Le professionnel doit protéger la personne ET se protéger lui-même." },
  ]},

  // =============================================
  // 6. CONDUITES À RISQUE ET HYGIÈNE DE VIE
  // =============================================
  { type: 'section', section: "6", title: "Conduites à risque et hygiène de vie" },
  { type: 'richtext', title: "Impact des habitudes de vie", blocks: [
    { text: "Certaines habitudes impactent la santé :", list: [
      "Alimentation déséquilibrée",
      "Manque de sommeil",
      "Sédentarité",
      "Tabac, alcool",
      "Usage excessif des écrans",
    ]},
    { text: "Le professionnel ASSP joue un rôle éducatif (lien avec le Bloc 4) :", list: [
      "Sensibiliser un adolescent aux écrans",
      "Encourager l'hydratation chez la personne âgée",
    ]},
  ]},

  // =============================================
  // 7. ENVIRONNEMENT ET DÉVELOPPEMENT DURABLE
  // =============================================
  { type: 'section', section: "7", title: "Environnement et développement durable" },
  { type: 'richtext', title: "Agir pour l'environnement", blocks: [
    { text: "Le professionnel agit aussi sur l'environnement :", list: [
      "Tri des déchets",
      "Gestion des DASRI (Déchets d'Activités de Soins à Risques Infectieux)",
      "Économie d'eau",
      "Utilisation raisonnée des produits",
    ]},
    { note: "Objectif : protéger la santé + limiter l'impact environnemental." },
  ]},

  // =============================================
  // 8. CONDUITE À TENIR EN CAS D'URGENCE
  // =============================================
  { type: 'section', section: "8", title: "Conduite à tenir en cas d'urgence" },
  { type: 'richtext', title: "PAS : Protéger – Alerter – Secourir", blocks: [
    { heading: "1. Protéger", text: "Sécuriser la zone pour éviter le suraccident." },
    { heading: "2. Alerter", text: "Appeler les secours : 15 (SAMU) / 18 (Pompiers) / 112 (Urgences européennes)." },
    { heading: "3. Secourir", text: "Gestes adaptés : PLS (Position Latérale de Sécurité), surveillance, gestes d'urgence en cas d'étouffement..." },
    { note: "Exemple : un enfant s'étouffe → réaction rapide = vitale." },
  ]},

  // =============================================
  // 9. CAS PRATIQUES PROFESSIONNELS
  // =============================================
  { type: 'section', section: "9", title: "Cas pratiques professionnels" },
  { type: 'richtext', title: "Cas 1 – Chute en EHPAD", blocks: [
    { text: "Une résidente chute dans sa chambre." },
    { heading: "Analyse", text: "Sol encombré, éclairage insuffisant." },
    { heading: "Actions", text: "Sécurisation de la zone, transmission à l'équipe, mesures de prévention future (désencombrement, éclairage renforcé)." },
  ]},
  { type: 'richtext', title: "Cas 2 – Fatigue professionnelle", blocks: [
    { text: "Une aide à domicile est épuisée, stressée." },
    { heading: "Risque", text: "Burn-out, erreur professionnelle." },
    { heading: "Solutions", text: "Communication avec l'équipe, réorganisation des plannings, soutien professionnel." },
  ]},
  { type: 'richtext', title: "Cas 3 – Infection en crèche", blocks: [
    { text: "Plusieurs enfants tombent malades." },
    { heading: "Analyse", text: "Hygiène des mains insuffisante." },
    { heading: "Action", text: "Protocole de lavage renforcé, sensibilisation des équipes et des familles." },
  ]},

  // ===== CONCLUSION =====
  { type: 'richtext', title: "Conclusion du module PSE", blocks: [
    { text: "Le PSE permet de comprendre que la prévention est au cœur du métier. Le professionnel ASSP doit être capable de :", list: [
      "Identifier les risques",
      "Analyser les situations (méthode 5M)",
      "Agir pour prévenir",
      "Protéger la personne et se protéger lui-même",
    ]},
    { note: "La prévention n'est pas une contrainte, c'est une compétence essentielle du métier." },
  ]},

  // ===== FÉLICITATIONS =====
  { type: 'congratulations', title: "Félicitations !" },
];

const BACASSPPSEPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewedSlides, setViewedSlides] = useState(new Set([0]));
  const audioRef = useRef(null);
  const API_URL = window.location.origin || '';

  useEffect(() => {
    const saved = getActivityProgress(ACTIVITY_ID);
    if (saved?.viewedSlides) setViewedSlides(new Set(saved.viewedSlides));
  }, []);

  const speakText = async (text) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/tts/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: text.substring(0, 800), voice: 'nova', speed: 1.1 }) });
      if (r.ok) { const d = await r.json(); if (d.audio_url || d.audio_base64) { if (audioRef.current) audioRef.current.pause(); audioRef.current = new Audio(d.audio_url || `data:audio/mp3;base64,${d.audio_base64}`); audioRef.current.play(); setIsPlaying(true); audioRef.current.onended = () => setIsPlaying(false); } }
    } catch (e) { console.log('TTS unavailable'); }
    setIsLoading(false);
  };
  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setIsPlaying(false); } };
  const getSlideText = (s) => { let t = s.title || ''; if (s.blocks) s.blocks.forEach(b => { if (b.heading) t += ' ' + b.heading; if (b.text) t += ' ' + b.text; if (b.list) b.list.forEach(li => t += ' ' + li); }); return t; };

  const nextSlide = () => { if (currentSlide < slides.length - 1) { const next = currentSlide + 1; setCurrentSlide(next); setViewedSlides(prev => new Set([...prev, next])); markSlideViewed(ACTIVITY_ID, next, slides.length); } };
  const prevSlide = () => { if (currentSlide > 0) setCurrentSlide(currentSlide - 1); };
  const goToSlide = (i) => { setCurrentSlide(i); setViewedSlides(prev => new Set([...prev, i])); markSlideViewed(ACTIVITY_ID, i, slides.length); };

  const progressPercent = Math.round((viewedSlides.size / slides.length) * 100);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex items-center justify-center h-full relative" style={{ background: theme.bg }}>
            <img src={slide.image} alt="PSE" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            {slide.blocTitle && (
              <div className="absolute bottom-0 left-0 right-0 text-center" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', padding: '2rem 1rem 1.5rem' }}>
                <h1 className="text-white font-bold text-lg md:text-xl drop-shadow-lg">{slide.blocTitle}</h1>
              </div>
            )}
          </div>
        );
      case 'sommaire':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(219,234,254,0.88)' }} />
            <div className="relative z-10 h-full flex flex-col">
              <div style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`, padding: '0.6rem 0' }}>
                <h2 className="text-xl font-bold text-center text-white tracking-widest uppercase">Sommaire</h2>
              </div>
              <div className="flex-1 overflow-auto px-4 flex items-center justify-center">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl mx-auto">
                  {slide.sections.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-white/90 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all backdrop-blur-sm" style={{ border: '1px solid #e0e0e0' }} onClick={() => goToSlide(s.target - 1)}>
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white" style={{ background: theme.primary }}>{s.num}</span>
                      <span className="text-gray-700 text-sm leading-tight font-medium">{s.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(29,78,216,0.45)' }} />
            <div className="text-center px-10 relative z-10">
              <span className="text-white/80 text-2xl md:text-3xl font-bold mb-3 block tracking-wide">Chapitre {slide.section}</span>
              <h2 className="text-xl md:text-2xl font-semibold text-white drop-shadow-lg">{slide.title}</h2>
            </div>
          </div>
        );
      case 'subsection':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(29,78,216,0.35)' }} />
            <div className="text-center px-10 relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">{slide.title}</h2>
            </div>
          </div>
        );
      case 'richtext':
        return (
          <div className="h-full overflow-auto flex items-center justify-center p-6" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-4 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="space-y-3">
                {slide.blocks.map((block, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 shadow-sm" style={{ border: `1px solid ${theme.primary}12` }}>
                    {block.heading && <h4 className="font-semibold mb-2 text-[15px]" style={{ color: theme.primary }}>{block.heading}</h4>}
                    {block.text && <p className="text-gray-700 text-[15px] leading-relaxed mb-2">{block.text}</p>}
                    {block.list && (
                      <div className="space-y-1.5">
                        {block.list.map((li, j) => (
                          <div key={j} className="flex items-start gap-2.5">
                            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: theme.primary }} />
                            <span className="text-gray-600 text-[15px] leading-relaxed">{li}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {block.note && <p className="text-[14px] italic mt-2.5 p-2.5 rounded-md" style={{ background: `${theme.primary}08`, borderLeft: `3px solid ${theme.primary}`, color: '#475569' }}>{block.note}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'congratulations':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(29,78,216,0.5)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-2xl">
              <div className="text-5xl mb-4">&#127942;</div>
              <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">{slide.title}</h2>
              <p className="text-white text-lg mb-3 leading-relaxed drop-shadow">Vous avez terminé le module <strong>PSE</strong> du BAC ASSP.</p>
              <p className="text-white text-base mb-6 leading-relaxed drop-shadow">Vous maîtrisez les risques professionnels, la méthode 5M, la prévention et la conduite à tenir en urgence.</p>
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-base font-semibold mb-1" style={{ color: theme.primary }}>Testez vos connaissances !</p>
                <p className="text-sm text-gray-600">Rendez-vous dans le <strong>Quiz - PSE</strong> pour évaluer votre maîtrise.</p>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  const slide = slides[currentSlide];
  return (
    <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: theme.bg }} data-testid="bac-assp-pse-presentation">
      <div style={{ minHeight: '480px', height: '480px' }}>{renderSlide(slide)}</div>
      <div className="w-full h-2 bg-gray-200">
        <div className="h-full rounded-r-full transition-all duration-300" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} />
      </div>
      <div className="flex items-center justify-between p-3 bg-white border-t" style={{ borderColor: theme.primary + '30' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40" style={{ borderColor: theme.primary, color: theme.text }} data-testid="pse-prev-btn">
          <ChevronLeft size={16} /> Précédent
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: theme.primary }} data-testid="pse-audio-btn">
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="font-bold text-sm" style={{ color: theme.primary }}>{currentSlide + 1} / {slides.length}</span>
          <span className="text-xs text-gray-400">{progressPercent}%</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40" style={{ background: theme.primary }} data-testid="pse-next-btn">
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default BACASSPPSEPresentation;
